import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, EmptyState, Inline, Panel, Skeleton, t, Text, toast } from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { ListTable, type ListResult } from './ListTable';
import { api, ApiFailure, unwrap } from '../lib/api';
import { relativeTime, sharedPrefix, shortDate } from '../lib/format';
import { uptime } from '../lib/uptime';
import { endedIsDefect, endedOf, named, technologiesLine, technologiesText, vulnerabilityTone, vulnerabilityTotal } from '../lib/technologies';
import { environmentColumn } from './ServerState';
import { MoreCount } from './MoreCount';
import { useEnvironmentName } from '../lib/environments';
import { type DotSpec, HealthLegend, healthColumn } from './StateColumn';
import { PendingButton } from './PendingButton';
import { AttachSiteDialog } from './AttachSiteDialog';

type Running = fleetState.components['schemas']['Running'];
type Site = Running['sites'][number];
type Container = Running['containers'][number];
type HostUnit = NonNullable<Running['units']>[number];
type Port = Running['ports'][number];
type Update = NonNullable<Running['updates']>[number];

export type RunningSection = 'updates' | 'domains' | 'containers' | 'ports' | 'offPackage';
export type { Running, Site, Container, HostUnit };
const UNIT_STATES = ['active', 'inactive', 'failed', 'activating', 'deactivating'];
const CONTAINER_STATES = ['running', 'exited', 'paused', 'restarting', 'created', 'dead'];

/**
 * D53: a container's health as one dot, run state and Docker healthcheck
 * together (the only place this mapping lives). Its word is the tooltip,
 * Docker's own status (« Up 3 days (healthy) ») the second line.
 */
const CONTAINER_DOTS = {
  healthy: { tone: 'pos', variant: 'solid' },
  running: { tone: 'pos', variant: 'ring' },
  starting: { tone: 'warn', variant: 'solid' },
  paused: { tone: 'info', variant: 'solid' },
  unhealthy: { tone: 'neg', variant: 'halo' },
  stopped: { tone: 'neg', variant: 'halo' },
  done: { tone: 'neutral', variant: 'ring' },
} as const;
type ContainerDot = keyof typeof CONTAINER_DOTS;
const CONTAINER_ORDER: ContainerDot[] = ['unhealthy', 'stopped', 'starting', 'paused', 'running', 'healthy', 'done'];

function containerDot(c: Container): ContainerDot {
  const up = uptime(c.status);
  // D48: a one-off task that ended well is done, not stopped.
  if (c.brick?.type === 'task' && !c.brick.fault && c.state === 'exited') return 'done';
  if (c.state === 'running') return up.health === 'unhealthy' ? 'unhealthy' : up.health === 'starting' ? 'starting' : up.health === 'healthy' ? 'healthy' : 'running';
  if (c.state === 'restarting') return 'starting';
  if (c.state === 'paused' || c.state === 'created') return 'paused';
  return 'stopped';
}

function containerSpec(c: Container): DotSpec {
  const dot = containerDot(c);
  // The word of the state Docker gives (« Arrêté », « Hors service »), the dot's own word when the healthcheck says more.
  const label = dot === 'stopped' || dot === 'paused'
    ? (CONTAINER_STATES.includes(c.state) ? t(`running.containers.states.${c.state}`) : c.state)
    : t(`running.containers.dots.${dot}`);
  return { ...CONTAINER_DOTS[dot], label, description: c.status || undefined };
}

const CONTAINER_LEGEND = (): DotSpec[] => CONTAINER_ORDER.map(dot => ({ ...CONTAINER_DOTS[dot], label: t(`running.containers.dots.${dot}`), description: t(`running.containers.dotsHelp.${dot}`) }));

/** D53: a systemd service or timer, or a cron job, as one dot (the only place this mapping lives). */
const UNIT_DOTS = {
  fault: { tone: 'neg', variant: 'halo' },
  changing: { tone: 'warn', variant: 'solid' },
  stopped: { tone: 'warn', variant: 'solid' },
  waiting: { tone: 'info', variant: 'solid' },
  active: { tone: 'pos', variant: 'solid' },
  notWatched: { tone: 'neutral', variant: 'ring' },
} as const;
type UnitDot = keyof typeof UNIT_DOTS;
const UNIT_ORDER: UnitDot[] = ['fault', 'changing', 'stopped', 'waiting', 'active', 'notWatched'];

function unitDot(u: HostUnit): { dot: UnitDot; fault: string | null } {
  if (u.kind === 'cron') return { dot: 'notWatched', fault: null };
  const fault = u.brick?.fault ?? (u.state === 'failed' ? 'failed' : null);
  if (fault) return { dot: 'fault', fault };
  if (u.state === 'active') return { dot: 'active', fault: null };
  if (u.state === 'activating' || u.state === 'deactivating') return { dot: 'changing', fault: null };
  // A timer between two runs waits; a service that does not run is stopped.
  return { dot: u.kind === 'timer' ? 'waiting' : 'stopped', fault: null };
}

function unitSpec(u: HostUnit): DotSpec {
  const { dot, fault } = unitDot(u);
  const label = dot === 'notWatched' ? t('running.units.notWatched')
    : fault ? t(`running.units.fault.${fault}`)
      : dot === 'waiting' ? t('running.units.dots.waiting')
        : UNIT_STATES.includes(u.state ?? '') ? t(`running.units.states.${u.state}`) : (u.state ?? t(`running.units.dots.${dot}`));
  return { ...UNIT_DOTS[dot], label, description: dot === 'notWatched' ? t('running.units.dotsHelp.notWatched') : u.state ?? undefined };
}

const UNIT_LEGEND = (): DotSpec[] => UNIT_ORDER.map(dot => ({ ...UNIT_DOTS[dot], label: t(`running.units.dots.${dot}`), description: t(`running.units.dotsHelp.${dot}`) }));

/** Certificate expiry: J-21 warns, J-7 is an incident (D24, like the TLS checks of SPEC-008). */
function CertificateCell({ tls }: { tls: Site['tls'] }) {
  if (!tls) return <Text variant="body-sm" tone="mute" truncate>{t('running.sites.noTls')}</Text>;
  if (tls.daysLeft === null || !tls.notAfter) return <Text variant="body-sm" tone="mute" truncate>{t('running.sites.tlsUnread')}</Text>;
  const date = shortDate(tls.notAfter);
  if (tls.daysLeft < 0) return <Badge tone="neg" size="sm">{t('running.sites.expired', { date })}</Badge>;
  if (tls.daysLeft < 21) return <Badge tone={tls.daysLeft < 7 ? 'neg' : 'warn'} size="sm">{t('running.sites.expiresIn', { n: tls.daysLeft })}</Badge>;
  return <Text variant="body-sm" truncate>{t('running.sites.until', { date })}</Text>;
}

/** One line: the first item, then « +N » for the others (all of them stay searchable). */
function FirstAndMore({ first, more, strong }: { first: string; more: number; strong?: boolean }) {
  return (
    <Inline gap={8} align="center" wrap={false}>
      <Text variant="code" strong={strong} truncate>{first}</Text>
      <MoreCount n={more} />
    </Inline>
  );
}

/** Where the site sends its requests: its first container and how many more (D48), an upstream, or files. */
function DestinationCell({ site }: { site: Site }) {
  const first = site.destination?.container ?? site.containers[0] ?? site.destination?.upstream;
  if (!first) return <Text variant="body-sm" tone="ink-2" truncate>{t('running.sites.static')}</Text>;
  return <FirstAndMore first={first} more={site.containers.filter(c => c !== first).length} />;
}

const attachedLabel = (s: Site) => s.attachedTo && `${s.attachedTo.clientTitle} › ${s.attachedTo.projectTitle} · ${s.attachedTo.application}`;

/** D39: the WordPress install a site serves, and how many vulnerabilities are known against it. */
function wordpressOf(wordpress: Running['wordpress'], s: Site) {
  const wp = wordpress?.find(w => w.domains.some(d => s.domains.includes(d)));
  return wp ? { wp, known: wp.core.length + [...wp.plugins, ...wp.themes].reduce((n, e) => n + e.vulnerabilities.length, 0) } : null;
}

/** The sites the agent reads, one idea per column (D27); shared by the « Applications » blocks (D47). */
/** D120: the rows of one table of « Ce qui tourne », searched, filtered and paged by the server. */
export const runningList = <T,>(server: string, list: 'sites' | 'containers' | 'units' | 'updates' | 'ports' | 'offPackage' | 'wordpress' | 'php' | 'wordpressFindings' | 'technologies' | 'cves' | 'ignored') =>
  async (query: Record<string, unknown>) => unwrap(await api.fleetState.GET('/servers/{server}/running/{list}', { params: { path: { server, list }, query: query as never } })) as unknown as ListResult<T>;

export function SitesTable({ label, server, deployment, wordpress, onAttach }: { label: string; server: string; deployment?: string; wordpress: Running['wordpress']; onAttach?: (s: Site) => void }) {
  const envName = useEnvironmentName();
  return (
            <ListTable<Site>
              label={label}
              queryKey={['server-running-list', server, 'sites', deployment ?? null]}
              fetch={runningList<Site>(server, 'sites')}
              fixed={deployment ? { deployment } : undefined}
              rowKey={(s: Site) => `${s.server}:${s.domains.join(',')}`}
              defaultSort={{ key: 'domains', dir: 'asc' }}
              search={t('running.sites.search')}
              facets={[
                { key: 'certificate', label: t('running.sites.certificate'), valueLabel: v => t(`running.sites.certificates.${v}`) },
                { key: 'server', label: t('running.sites.server') },
                { key: 'deployment', label: t('running.sites.attachment'), valueLabel: v => (v === 'none' ? t('running.notAttached') : v) },
              ]}
              refetchInterval={60_000}
              columns={sites => {
              // Within a deployment's block every site is attached to it, and in « Non rattaché » none is:
              // the block's title already says so, the column stays for « Rattacher » or a mix.
              const oneDeployment = Boolean(deployment);
              const anyAttached = sites.some(s => s.attachedTo);
              const attachment = !oneDeployment && (anyAttached || Boolean(onAttach));
              return ([
                { key: 'domains', label: t('running.sites.domains'), pinned: true, grow: true, width: 260, accessor: (s: Site) => s.domains.join(' '), render: (s: Site) => (
                  <FirstAndMore first={s.domains[0] ?? '—'} more={s.domains.length - 1} strong />
                ) },
                { key: 'tls', label: t('running.sites.certificate'), width: 160, render: (s: Site) => <CertificateCell tls={s.tls} /> },
                {
                  key: 'destination', label: t('running.sites.destination'), width: 220,
                  accessor: (s: Site) => [...s.containers, s.destination?.upstream ?? ''].join(' '),
                  render: (s: Site) => <DestinationCell site={s} />,
                },
                { key: 'ports', label: t('running.sites.ports'), width: 90, mono: true, sortable: false, accessor: (s: Site) => s.ports.join(', ') },
                { key: 'server', label: t('running.sites.server'), width: 110, accessor: (s: Site) => s.server },
                // D39: the WordPress install the site serves; D58: its known vulnerabilities in their own column (detail in « Logiciels »).
                ...(wordpress?.length ? [
                  { key: 'wordpress', label: t('running.sites.application'), width: 140, sortable: false,
                    accessor: (s: Site) => { const w = wordpressOf(wordpress, s); return w ? t('running.sites.wordpress', { version: w.wp.version }) : '—'; } },
                  { key: 'wordpressVulnerabilities', label: t('running.sites.vulnerabilities'), width: 120, sortable: false,
                    accessor: (s: Site) => { const w = wordpressOf(wordpress, s); return w && w.known > 0 ? t('running.technologies.vulnerabilities', { n: w.known }) : ''; },
                    render: (s: Site) => { const w = wordpressOf(wordpress, s); return w && w.known > 0 ? <Badge tone="neg" size="sm">{t('running.technologies.vulnerabilities', { n: w.known })}</Badge> : null; } },
                ] : []),
                // Last: the deployment the site belongs to, or « Rattacher » (its action).
                // D58: its environment in the next column, never a badge beside the deployment's name.
                ...(attachment ? [{
                  key: 'attachedTo', label: t('running.sites.attachment'), width: anyAttached ? 280 : 130, sortable: false, accessor: (s: Site) => attachedLabel(s) || '',
                  render: (s: Site) => (s.attachedTo ? <Text variant="body-sm" truncate>{attachedLabel(s)}</Text>
                    : onAttach
                      ? <Button variant="tonal" size="sm" onClick={() => onAttach(s)} aria-label={t('running.attachSite', { domain: s.domains[0] ?? '' })}>{t('running.attach')}</Button>
                      : <Badge tone="warn" size="sm">{t('running.notAttached')}</Badge>),
                }, ...(anyAttached ? [{ ...environmentColumn<Site>(t('running.sites.environment'), envName,
                  s => (s.attachedTo ? { environment: s.attachedTo.environment, type: s.attachedTo.environmentType } : null)), sortable: false }] : [])] : []),
              ]); }}
              empty={<EmptyState framed={false} icon="language" title={t('running.sites.empty')} />}
            />
  );
}

type Brick = NonNullable<Container['brick']>;
interface OffPackageRow { id: string; kind: 'service' | 'executable'; name: string; detail: string }

/**
 * D48: what the brick is for its deployment (service, data, one-off task), and
 * its name only when the row's own name does not already say it.
 */
function BrickCell({ brick, owner }: { brick: Brick | null; owner: string }) {
  if (!brick) return <Text variant="body-sm" tone="ink-2">—</Text>;
  return (
    <Inline gap={8} align="center" wrap={false}>
      <Badge tone="neutral" size="sm">{t(`bricks.type.${brick.type}`)}</Badge>
      {!namedBy(owner, brick) && <Text variant="code" truncate>{brick.name}</Text>}
    </Inline>
  );
}
const namedBy = (owner: string, brick: Brick) => owner.toLowerCase().includes(brick.name.toLowerCase());
/** The « Brique » column: the type badge alone is narrow, a differing name widens it. */
const brickWidth = (rows: { name: string; brick: Brick | null }[]) => (rows.some(r => r.brick && !namedBy(r.name, r.brick)) ? 200 : 110);

/**
 * D46: what the container's image runs, on one line — « Node 20.11 · Next.js 14 »,
 * (D60) « PostgreSQL 16.4 » when it runs a service and no runtime, « +2 » for the others (named in its tooltip). D58: the known vulnerabilities
 * and the end of life in their own columns (TechnologiesVulnerabilities,
 * TechnologiesEndOfLife), never badges after the text.
 */
function TechnologiesCell({ technologies: x }: { technologies: Container['technologies'] }) {
  if (!x) return <Text variant="body-sm" tone="ink-2">—</Text>;
  const { text, others } = technologiesLine(x);
  if (x.status === 'pending' && !text) return <Text variant="body-sm" tone="mute" truncate>{t('running.technologies.pending')}</Text>;
  return (
    <Inline gap={8} align="center" wrap={false}>
      {text
        ? <Text variant="body-sm" truncate>{text}</Text>
        : <Text variant="body-sm" tone="mute" truncate>{t('running.technologies.none')}</Text>}
      <MoreCount n={others.length} title={others.map(o => named(o)).join(' · ')} />
    </Inline>
  );
}

/** The image's known vulnerabilities (D46), or why there is no count: analysed again (D55) or not analysed. */
function vulnerabilitiesState(x: Container['technologies']) {
  if (!x) return null;
  const total = vulnerabilityTotal(x.vulnerabilities);
  if (total > 0 && x.vulnerabilities) return { kind: 'count' as const, total, label: t('running.technologies.vulnerabilities', { n: total }) };
  if (x.status === 'failed' && !x.vulnerabilities) return { kind: 'failed' as const, total: -1, label: t('running.technologies.notAnalysed') };
  if (x.status === 'pending' && x.analysedAt) return { kind: 'pending' as const, total: -1, label: t('running.technologies.reanalysing') };
  return null;
}

function TechnologiesVulnerabilities({ technologies: x }: { technologies: Container['technologies'] }) {
  const state = vulnerabilitiesState(x);
  if (!x || !state) return null;
  if (state.kind === 'failed') {
    return <Badge tone="neutral" size="sm" title={t(x.error === 'database' ? 'running.technologies.error.database' : 'running.technologies.error.scan')}>{state.label}</Badge>;
  }
  if (state.kind === 'pending') return <Badge tone="info" size="sm" title={t('running.technologies.reanalysingHelp')}>{state.label}</Badge>;
  return (
    <Badge tone={vulnerabilityTone(x.vulnerabilities)} size="sm"
      title={[
        t('running.technologies.severities', x.vulnerabilities!),
        x.analysedAt ? t('running.technologies.analysedAt', { when: relativeTime(x.analysedAt) }) : '',
        // D55: analysed again after « Relancer l'analyse »: the count is the previous analysis'.
        x.status === 'pending' ? t('running.technologies.reanalysingHelp') : '',
      ].filter(Boolean).join(' — ')}>
      {state.label}
    </Badge>
  );
}

/** « Fin de vie » when a runtime or (D60) a service (red, a defect of the application, D47) or a framework (orange) is past its end of support. */

function TechnologiesEndOfLife({ technologies: x }: { technologies: Container['technologies'] }) {
  const ended = endedOf(x);
  if (!x || !ended.length) return null;
  return (
    <Badge tone={endedIsDefect(x) ? 'neg' : 'warn'} size="sm"
      title={ended.map(e => (e.endOfLife ? t('running.technologies.endedSince', { name: named(e), date: shortDate(e.endOfLife) }) : t('running.technologies.ended', { name: named(e) }))).join(' · ')}>
      {t('running.technologies.endOfLife')}
    </Badge>
  );
}

/**
 * The containers the agent reads; shared by the « Applications » blocks (D47).
 * D51: `onOpen` makes a row open its technologies in detail (application page).
 */
export function ContainersTable({ label, server, deployment, onOpen, active }: { label: string; server: string; deployment?: string; onOpen?: (c: Container) => void; active?: string | null }) {
  return (
            <ListTable<Container>
              label={label}
              queryKey={['server-running-list', server, 'containers', deployment ?? null]}
              fetch={runningList<Container>(server, 'containers')}
              fixed={deployment ? { deployment } : undefined}
              rowKey="name"
              defaultSort={{ key: 'name', dir: 'asc' }}
              search={t('running.containers.search')}
              facets={[
                { key: 'state', label: t('running.containers.state') },
                { key: 'brick', label: t('running.containers.brick'), valueLabel: v => t(`bricks.type.${v}`) },
              ]}
              refetchInterval={60_000}
              {...(onOpen ? { onRowClick: onOpen } : {})}
              {...(active ? { activeRow: (c: Container) => c.name === active } : {})}
              columns={containers => {
              // The names start alike (the stack's name): the column shows what tells them apart, the full name on hover and in search.
              const prefix = sharedPrefix(containers.map(c => c.name));
              return ([
                healthColumn<Container>({ legendLabel: t('running.containers.legend'), legendTitle: t('running.containers.legendTitle'), legend: <HealthLegend items={CONTAINER_LEGEND()} />, spec: containerSpec }),
                { key: 'name', label: t('running.containers.name'), mono: true, strong: true, pinned: true, grow: true, width: 240,
                  ...(prefix ? { render: (c: Container) => <span title={c.name}>{c.name.slice(prefix.length)}</span> } : {}) },
                { key: 'uptime', label: t('running.containers.uptime'), width: 130, align: 'right', sortable: false, accessor: (c: Container) => uptime(c.status).label },
                ...(containers.some(c => c.brick) ? [{ key: 'brick', label: t('running.containers.brick'), width: brickWidth(containers), sortable: false, accessor: (c: Container) => c.brick?.name ?? '', render: (c: Container) => <BrickCell brick={c.brick} owner={c.name} /> }] : []),
                // D46: shown once the agent reads them (running v7): before, the column would only say « — ».
                // D58: the technologies, then their known vulnerabilities and end of life, each in its own column.
                ...(containers.some(c => c.technologies) ? [{
                  key: 'technologies', label: t('running.technologies.column'), width: 240, sortable: false,
                  accessor: (c: Container) => technologiesText(c.technologies),
                  render: (c: Container) => <TechnologiesCell technologies={c.technologies} />,
                }, {
                  key: 'vulnerabilities', label: t('running.technologies.vulnerabilitiesColumn'), width: 130, sortable: false,
                  accessor: (c: Container) => vulnerabilitiesState(c.technologies)?.label ?? '',
                  render: (c: Container) => <TechnologiesVulnerabilities technologies={c.technologies} />,
                }, {
                  key: 'endOfLife', label: t('running.technologies.endOfLife'), width: 110, sortable: false,
                  accessor: (c: Container) => (endedOf(c.technologies).length ? t('running.technologies.endOfLife') : ''),
                  render: (c: Container) => <TechnologiesEndOfLife technologies={c.technologies} />,
                }] : []),
                { key: 'image', label: t('running.containers.image'), mono: true, width: 260 },
                { key: 'ports', label: t('running.containers.ports'), width: 170, mono: true, sortable: false, accessor: (c: Container) => c.ports.join(', ') || '—' },
                { key: 'site', label: t('running.containers.site'), width: 200, mono: true, sortable: false, accessor: (c: Container) => c.site ?? '—' },
              ]); }}
              empty={<EmptyState framed={false} icon="deployed_code" title={t('running.containers.empty')} />}
            />
  );
}

/**
 * D48: the systemd services and timers written by hand, and the cron jobs no
 * package ships (their program only), with the brick each one is.
 */
export function UnitsTable({ label, server, deployment }: { label: string; server: string; deployment?: string }) {
  return (
    <ListTable<HostUnit>
      label={label}
      queryKey={['server-running-list', server, 'units', deployment ?? null]}
      fetch={runningList<HostUnit>(server, 'units')}
      fixed={deployment ? { deployment } : undefined}
      rowKey="key"
      defaultSort={{ key: 'name', dir: 'asc' }}
      search={t('running.units.search')}
      facets={[{ key: 'kind', label: t('running.units.kind'), valueLabel: v => t(`running.units.kinds.${v}`) }]}
      refetchInterval={60_000}
      columns={units => [
        healthColumn<HostUnit>({ legendLabel: t('running.units.legend'), legendTitle: t('running.units.legendTitle'), legend: <HealthLegend items={UNIT_LEGEND()} />, spec: unitSpec }),
        { key: 'name', label: t('running.units.name'), mono: true, strong: true, pinned: true, grow: true, width: 260 },
        { key: 'kind', label: t('running.units.kind'), width: 150, accessor: (u: HostUnit) => t(`running.units.kinds.${u.kind}`) },
        ...(units.some(u => u.brick) ? [{ key: 'brick', label: t('running.containers.brick'), width: brickWidth(units), sortable: false, accessor: (u: HostUnit) => u.brick?.name ?? '', render: (u: HostUnit) => <BrickCell brick={u.brick} owner={u.name} /> }] : []),
        { key: 'detail', label: t('running.units.detail'), width: 240, mono: true, sortable: false, accessor: (u: HostUnit) => u.detail || '—' },
      ]}
      empty={<EmptyState framed={false} icon="schedule" title={t('running.units.empty')} />}
    />
  );
}

/**
 * SPEC-032: what the Checkmk agent reads on the server, set against the
 * declared deployments, one server tab per section (D27): Domaines,
 * Conteneurs, Ports, Hors paquets. Read only; « Rattacher » opens the pull
 * request of SPEC-006, pre-filled.
 */
export function RunningPanel({ server, section, canReapply: reapplyAllowed, onReapply }: { server: string; section: RunningSection; canReapply: boolean; onReapply: () => void }) {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [attaching, setAttaching] = useState<Site | null>(null);
  const key = ['server-running', server];
  const running = useQuery({
    queryKey: key,
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/running', { params: { path: { server } } })),
    refetchInterval: 60_000,
  });
  const refresh = async () => {
    setRefreshing(true);
    try {
      queryClient.setQueryData(key, unwrap(await api.fleetState.POST('/servers/{server}/running/refresh', { params: { path: { server } } })));
      toast({ tone: 'pos', title: t('running.refreshed') });
    } catch (e) {
      toast({ tone: 'neg', title: e instanceof ApiFailure ? e.body.message : t('running.refreshFailed') });
    } finally {
      setRefreshing(false);
    }
  };
  const r = running.data;
  const reapply = r?.canReapply && reapplyAllowed
    ? <Button variant="secondary" size="sm" icon="replay" onClick={onReapply}>{t('running.reapply')}</Button>
    : undefined;

  const panel = (children: React.ReactNode) => (
    <Panel
      title={t(`running.section.${section}`)}
      subtitle={[
        // D29: the count first, said as people say it.
        section === 'updates' && r?.updates?.length ? `${t('running.updates.count', { n: r.updates.length })}, ${r.updates.some(u => u.security) ? t('running.updates.withSecurity', { n: r.updates.filter(u => u.security).length }) : t('running.updates.noSecurity')}` : null,
        // The software list is read at the enrolment, not each minute: its date, not the agent's last reading.
        section === 'offPackage' && r?.offPackage
          ? t('running.offPackage.collected', { when: shortDate(r.offPackage.collectedAt) })
          : r?.readAt ? t('running.readAt', { when: relativeTime(r.readAt) }) : t('running.subtitle'),
      ].filter(Boolean).join(' · ')}
      actions={(
        <Inline gap={8}>
          {(r?.installed || r?.pending) && r.canRefresh && <PendingButton variant="ghost" size="sm" icon="sync" pending={refreshing} onClick={() => void refresh()}>{t('running.refresh')}</PendingButton>}
        </Inline>
      )}
      flush
    >
      {children}
    </Panel>
  );
  if (running.isPending) return panel(<Skeleton lines={4} />);
  if (!r) return panel(<EmptyState framed={false} icon="error" title={t('running.failed')} />);
  if (!r.installed && r.pending) {
    return panel(
      <EmptyState framed={false} icon="hourglass_top" title={t('running.pending.title')}>
        {t('running.pending.body')}
      </EmptyState>,
    );
  }
  if (!r.installed) {
    return panel(
      <EmptyState framed={false} icon="sensors_off" title={t('running.notInstalled.title')} action={reapply}>
        {t('running.notInstalled.body')}
      </EmptyState>,
    );
  }

  return panel(
    <>
      {section === 'updates' && (r.updates ? (
        // D29: what the « Mises à jour » check counts, package by package.
        <ListTable<Update>
            label={t('running.section.updates')}
            queryKey={['server-running-list', server, 'updates']}
            fetch={runningList<Update>(server, 'updates')}
            rowKey="name"
            defaultSort={{ key: 'security', dir: 'asc' }}
            search={t('running.updates.search')}
            facets={[
              { key: 'security', label: t('running.updates.kind'), valueLabel: v => t(v === 'security' ? 'running.updates.security' : 'running.updates.normal') },
              { key: 'origin', label: t('running.updates.origin') },
            ]}
            refetchInterval={60_000}
            columns={[
              { key: 'name', label: t('running.updates.name'), mono: true, grow: true, width: 220 },
              { key: 'security', label: t('running.updates.kind'), width: 110, render: (u: Update) => (u.security
                ? <Badge tone="neg" size="sm">{t('running.updates.security')}</Badge>
                : <Badge tone="neutral" size="sm">{t('running.updates.normal')}</Badge>) },
              { key: 'installed', label: t('running.updates.installed'), mono: true, width: 180, sortable: false },
              { key: 'candidate', label: t('running.updates.candidate'), mono: true, width: 180, sortable: false },
              { key: 'origin', label: t('running.updates.origin'), mono: true, width: 200 },
            ]}
            empty={<EmptyState framed={false} icon="verified" title={t('running.updates.none')} />}
          />
      ) : (
        <EmptyState framed={false} icon="system_update_alt" title={t('running.updates.notRead.title')} action={reapply}>{t('running.updates.notRead.body')}</EmptyState>
      ))}
      {section === 'domains' && <SitesTable label={t('running.section.domains')} server={server} wordpress={r.wordpress} onAttach={r.canAttach ? setAttaching : undefined} />}
      {section === 'containers' && <ContainersTable label={t('running.section.containers')} server={server} />}
      {section === 'ports' && !r.restricted && (
              <ListTable<Port>
                label={t('running.section.ports')}
                queryKey={['server-running-list', server, 'ports']}
                fetch={runningList<Port>(server, 'ports')}
                rowKey={(p: Port) => `${p.address}:${p.port}:${p.process ?? ''}`}
                defaultSort={{ key: 'port', dir: 'asc' }}
                search={t('running.ports.search')}
                facets={[{ key: 'explainedBy', label: t('running.ports.explainedBy'), valueLabel: v => (v === 'none' ? t('running.ports.unexpected') : t(`running.ports.kinds.${v}`)) }]}
                refetchInterval={60_000}
                columns={[
                  { key: 'port', label: t('running.ports.port'), width: 180, mono: true, accessor: (p: Port) => `${p.address}:${p.port}` },
                  { key: 'explainedBy', label: t('running.ports.explainedBy'), grow: true, sortable: false, render: (p: Port) => (p.explainedBy
                    ? t(`running.ports.by.${p.explainedBy.kind}`, { name: p.explainedBy.name })
                    : <Badge tone="warn" size="sm">{t('running.ports.unexpected')}</Badge>) },
                  { key: 'process', label: t('running.ports.process'), width: 160, mono: true, render: (p: Port) => p.process ?? '—' },
                ]}
                empty={<EmptyState framed={false} icon="lan" title={t('running.ports.empty')} />}
              />
      )}
      {section === 'offPackage' && !r.restricted && (r.offPackage ? (
                <>
                  <ListTable<OffPackageRow>
                    label={t('running.section.offPackage')}
                    queryKey={['server-running-list', server, 'offPackage']}
                    fetch={runningList<OffPackageRow>(server, 'offPackage')}
                    rowKey="id"
                    defaultSort={{ key: 'name', dir: 'asc' }}
                    search={t('running.offPackage.search')}
                    facets={[{ key: 'kind', label: t('running.offPackage.kind'), valueLabel: v => t(`running.offPackage.${v}`) }]}
                    refetchInterval={false}
                    columns={[
                      { key: 'name', label: t('running.offPackage.name'), mono: true, strong: true, width: 240 },
                      { key: 'kind', label: t('running.offPackage.kind'), width: 150, render: (x: OffPackageRow) => t(`running.offPackage.${x.kind}`) },
                      { key: 'detail', label: t('running.offPackage.detail'), mono: true, grow: true, sortable: false },
                    ]}
                    empty={<EmptyState framed={false} icon="inventory_2" title={t('running.offPackage.empty')} />}
                  />
                </>
              ) : (
                <EmptyState framed={false} icon="inventory_2" title={t('running.offPackage.none')} />
              ))}
      {attaching && (
        <AttachSiteDialog
          server={server}
          site={attaching}
          onClose={() => setAttaching(null)}
          onOpened={() => { setAttaching(null); void queryClient.invalidateQueries({ queryKey: key }); }}
        />
      )}
    </>,
  );
}
